// A property of every code point, as `bun run unicode` writes it: the values it takes, once each, and the runs that
// take them. A run is where it starts — a delta from the run before, in base 36 — and which value it carries, and it
// holds until the next one starts, so the ends are the starts and 4144 categories are 17 kB rather than 150.
export interface RangeTable {
  values: string[];
  runs: string;
}

// The runs decoded into two arrays and searched, rather than a map of a million code points: the tables ride this
// page's chunk and are read for every character of every keystroke, so both the size and the lookup have to be small.
export function valueAt(table: RangeTable, code: number): string {
  const { starts, indexes } = decoded(table);
  let low = 0;
  let high = starts.length - 1;
  while (low < high) {
    const middle = (low + high + 1) >> 1;
    if (starts[middle] <= code) low = middle;
    else high = middle - 1;
  }
  return table.values[indexes[low]];
}

// The same runs read the other way round: every span the table holds, in the order the code points run. What the
// lookup above answers for one character, this answers for a page that wants the whole of a block at once.
export function spansOf(table: RangeTable): Span[] {
  const { starts, indexes } = decoded(table);
  return [...starts].map((start, at) => ({
    value: table.values[indexes[at]],
    start,
    end: at + 1 < starts.length ? starts[at + 1] - 1 : 0x10FFFF,
  }));
}

export interface Span {
  value: string;
  start: number;
  end: number;
}

interface Decoded {
  starts: Int32Array;
  indexes: Uint16Array;
}

// Decoded on the first question asked of a table and kept: there are four of them and they never change.
const readings = new Map<RangeTable, Decoded>();

function decoded(table: RangeTable): Decoded {
  const held = readings.get(table);
  if (held) return held;

  const runs = table.runs.split(" ");
  const reading: Decoded = { starts: new Int32Array(runs.length), indexes: new Uint16Array(runs.length) };
  let start = 0;
  for (const [at, run] of runs.entries()) {
    const [delta, index] = run.split(".");
    start += Number.parseInt(delta, 36);
    reading.starts[at] = start;
    reading.indexes[at] = Number.parseInt(index, 36);
  }

  readings.set(table, reading);
  return reading;
}
